/**
 * Account priority cluster colors — Triage / Defend / Monitor / Expand
 * by Account Health score bands (product AH thresholds / vendor_config).
 * Internal key `grow` displays as Monitor.
 */

import type { CSSProperties } from "react";

import {
  DEFAULT_ACCOUNT_HEALTH_THRESHOLDS,
  normalizeAccountHealthThresholds,
  type AccountHealthThresholds,
} from "../../Utils/HealthThresholds";

export const CLUSTER_ORDER = [
  "triage",
  "defend",
  "grow",
  "expand",
  "unscored",
] as const;

export type PortfolioClusterKey = (typeof CLUSTER_ORDER)[number];

/** Account Health score thresholds (product v3 defaults). */
export const AH_EXPAND = DEFAULT_ACCOUNT_HEALTH_THRESHOLDS.expand_min;
export const AH_GROW = DEFAULT_ACCOUNT_HEALTH_THRESHOLDS.delegate_min;
export const AH_DEFEND = DEFAULT_ACCOUNT_HEALTH_THRESHOLDS.defend_min;
export const AH_TRIAGE = DEFAULT_ACCOUNT_HEALTH_THRESHOLDS.triage_min;

export type ClusterPalette = {
  label: string;
  /** Solid accent (borders, dots, active chip fill) */
  color: string;
  /** Light tint background */
  bg: string;
  /** Readable text on light bg */
  text: string;
  /** Soft border for idle chips */
  borderLight: string;
};

/** Match Always On quadrant + Unavailable palette. */
export const CLUSTER_PALETTE: Record<PortfolioClusterKey, ClusterPalette> = {
  triage: {
    label: "Triage",
    color: "#ef4444",
    bg: "#fee2e2",
    text: "#dc2626",
    borderLight: "#fecaca",
  },
  defend: {
    label: "Defend",
    color: "#f97316",
    bg: "#ffedd5",
    text: "#ea580c",
    borderLight: "#fed7aa",
  },
  grow: {
    label: "Monitor",
    color: "#be9000",
    bg: "#fefce8",
    text: "#be9000",
    borderLight: "#f5e6a8",
  },
  expand: {
    label: "Expand",
    color: "#22c55e",
    bg: "#dcfce7",
    text: "#16a34a",
    borderLight: "#bbf7d0",
  },
  unscored: {
    label: "Unavailable",
    color: "#94a3b8",
    bg: "#f1f5f9",
    text: "#64748b",
    borderLight: "#e2e8f0",
  },
};

/**
 * Account Health → portfolio cluster by score band.
 * Uses rounded score so the label matches the bubble number.
 * Pass vendor_config thresholds to override product defaults.
 * `accountId` kept for call-site compatibility (unused).
 */
export function clusterFromAccountHealth(
  ah: number | null | undefined,
  _accountId?: string | null,
  thresholds?: Partial<AccountHealthThresholds> | null
): PortfolioClusterKey {
  if (ah == null || ah === 0 || !Number.isFinite(Number(ah))) return "unscored";
  const score = Math.round(Number(ah));
  if (score <= 0) return "unscored";
  const bands = normalizeAccountHealthThresholds(thresholds);
  if (score < bands.defend_min) return "triage";
  if (score < bands.delegate_min) return "defend";
  if (score < bands.expand_min) return "grow";
  return "expand";
}

/** Dot color aligned with AH cluster bands. */
export function accountHealthDotColor(
  score: number | null,
  thresholds?: Partial<AccountHealthThresholds> | null
): string {
  const key = clusterFromAccountHealth(score, undefined, thresholds);
  return CLUSTER_PALETTE[key].color;
}

export type ClusterChipStyle = {
  idle: { className: string; style: CSSProperties };
  active: { className: string; style: CSSProperties };
};

function buildChipStyles(palette: ClusterPalette): ClusterChipStyle {
  return {
    idle: {
      className: "",
      style: {
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        color: "#6b7280",
      },
    },
    active: {
      className: "font-extrabold",
      style: {
        backgroundColor: palette.bg,
        borderColor: palette.color,
        color: palette.text,
      },
    },
  };
}

const ALL_PALETTE: ClusterPalette = {
  label: "All",
  color: "#111827",
  bg: "#f3f4f6",
  text: "#111827",
  borderLight: "#d1d5db",
};

export const CLUSTER_CHIP_STYLES: Record<string, ClusterChipStyle> = {
  all: buildChipStyles(ALL_PALETTE),
  triage: buildChipStyles(CLUSTER_PALETTE.triage),
  defend: buildChipStyles(CLUSTER_PALETTE.defend),
  grow: buildChipStyles(CLUSTER_PALETTE.grow),
  expand: buildChipStyles(CLUSTER_PALETTE.expand),
  unscored: buildChipStyles(CLUSTER_PALETTE.unscored),
};

export const CLUSTER_SECTION_META: Record<
  PortfolioClusterKey,
  {
    label: string;
    action: string;
  }
> = {
  triage: {
    label: "Triage",
    action: "— Immediate attention needed",
  },
  defend: {
    label: "Defend",
    action: "— Protect and stabilize",
  },
  grow: {
    label: "Monitor",
    action: "— Deepen engagement",
  },
  expand: {
    label: "Expand",
    action: "— Accelerate and elevate",
  },
  unscored: {
    label: "Unavailable",
    action: "— KPI data not yet available",
  },
};
